<template>
    <lightbox
        :showing="showing"
        @update:showing="emitShowing($event)"
        @close="emitClose()">

        <swiper
            class="swiper"
            ref="swiper"
            :options="swiperOptions"
            @slide-change="onSlideChange()">

            <slot></slot>
        </swiper>

        <template slot="decorations">
            <!-- The following buttons are styled by the Vue Awesome Swiper
                 library. Both classes are needed. -->

            <button
                class="swiper-button-prev swiper-button-white"
                type="button"
                :disabled="!showSlideToPrev"
                @click.stop="slideToPrev()">
            </button>

            <button
                class="swiper-button-next swiper-button-white"
                type="button"
                :disabled="!showSlideToNext"
                @click.stop="slideToNext()">
            </button>
        </template>
    </lightbox>
</template>

<script>
    import { swiper } from 'vue-awesome-swiper';

    import Lightbox from './index';

    /**
     * Events:
     *  - update:index
     *  - update:showing
     *  - close (alias of update:showing)
     *  - slide-past-first
     *  - slide-past-last
     */

    export default {
        props: {
            showing: {
                type: Boolean,
                required: true,
            },

            index: {
                type: Number,
            },

            canSlidePastFirst: {
                type: Boolean,
                default: false,
            },

            canSlidePastLast: {
                type: Boolean,
                default: false,
            },
        },

        components: {
            swiper,
            Lightbox,
        },

        data() {
            return {
                showSlideToPrev: false,
                showSlideToNext: false,
            };
        },

        computed: {
            swiperOptions() {
                return {
                    initialSlide: this.index,
                    noSwipingClass: 'lightbox-gallery-no-swipe',
                };
            },
        },

        mounted() {
            if (this.showing) {
                this.updateSwiper();
            }
            window.addEventListener('keyup', this.onKeyup);
        },

        beforeDestroy() {
            window.removeEventListener('keyup', this.onKeyup);
        },

        methods: {
            onKeyup(ev) {
                if (!this.showing) {
                    return;
                }
                if (ev.key === 'ArrowLeft') {
                    this.slideToPrev();
                }
                if (ev.key === 'ArrowRight') {
                    this.slideToNext();
                }
            },

            onSlideChange() {
                this.emitIndex();
                this.updateNavMarkers();
            },

            emitIndex() {
                this.$emit('update:index', this.getSwiper().realIndex);
            },

            emitShowing(ev) {
                this.$emit('update:showing', ev);
            },

            emitClose() {
                this.$emit('close');
            },

            slideToPrev() {
                const swiper = this.getSwiper();

                if (!swiper.isBeginning) {
                    swiper.slidePrev();

                } else if (this.canSlidePastFirst) {
                    this.$emit('slide-past-first');
                }
            },

            slideToNext() {
                const swiper = this.getSwiper();

                if (!swiper.isEnd) {
                    swiper.slideNext();

                } else if (this.canSlidePastLast) {
                    this.$emit('slide-past-last');
                }
            },

            updateSwiper() {
                // Swiper doesn't like manual DOM manipulations done behind it's
                // back (fair enough), so whenever we add new slides, hide/show
                // it, etc. we should call this (according to the docs).
                this.getSwiper().update();
                this.$nextTick(() => {
                    this.updateNavMarkers();
                });
            },

            updateNavMarkers() {
                const swiper = this.getSwiper();

                if (!swiper.isBeginning) {
                    this.showSlideToPrev = true;
                } else {
                    this.showSlideToPrev = this.canSlidePastFirst;
                }

                if (!swiper.isEnd) {
                    this.showSlideToNext = true;
                } else {
                    this.showSlideToNext = this.canSlidePastLast;
                }
            },

            getSwiper() {
                return this.$refs.swiper && this.$refs.swiper.swiper;
            },
        },

        watch: {
            showing() {
                if (this.showing) {
                    this.updateSwiper();
                }
            },

            index() {
                if (typeof this.index === 'number') {
                    this.getSwiper().slideTo(this.index);
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    .swiper {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    }

    .swiper-button-white {
        background: none;
        border: none;

        &:disabled {
            opacity: 0.35;
            cursor: auto;
        }
    }
</style>



// WEBPACK FOOTER //
// gallery.vue?21883560